{
  "$id": "https://ophelos/config/journey.schema.json",
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "JourneyConfig",
  "description": "Workflow eligibility rules and journey transition configuration",
  "type": "object",
  "additionalProperties": false,
  "properties": {
    "auto_activate_journey": {
      "type": "boolean",
      "default": true,
      "description": "Whether to automatically activate cases from pending to awaiting_outreach after ingestion completes. Can be configured at operator, portfolio, client, account, or case level."
    },
    "auto_allocate_credit_cases": {
      "type": "boolean",
      "default": false,
      "description": "Whether incoming credit cases are automatically allocated against outstanding debit cases upon ingestion"
    },
    "payment_settings": {
      "type": "object",
      "description": "Payment configuration settings",
      "additionalProperties": false,
      "properties": {
        "allowPartialPayment": {
          "type": "boolean",
          "default": true,
          "description": "Allow customers to make partial payments"
        },
        "partialPaymentTab": {
          "type": "boolean",
          "default": true,
          "description": "Show partial payment as separate tab (true) or edit button (false)"
        },
        "allowAccountPartialPayment": {
          "type": "boolean",
          "default": false,
          "description": "Allow partial payments on the account-level (pay all) surface. Off by default and independent of case-level partial payments; requires allowPartialPayment and partialPaymentTab to also be on."
        },
        "allowFullPayLater": {
          "type": "boolean",
          "default": true,
          "description": "Allow customers to schedule full payment for a future date"
        },
        "allowPartialPayLater": {
          "type": "boolean",
          "default": true,
          "description": "Allow customers to schedule partial payment for a future date"
        },
        "enablePaymentPlan": {
          "type": "boolean",
          "default": true,
          "description": "Enable payment plan options"
        },
        "enableManualPlans": {
          "type": "boolean",
          "default": false,
          "description": "Allow payment methods that use manual plans (multibanco, mb_way)"
        },
        "maxDaysInFuture": {
          "type": "integer",
          "default": 30,
          "description": "Maximum days in the future a payment can be scheduled"
        },
        "minDebtForPartialPayment": {
          "type": "number",
          "default": 10,
          "description": "Minimum debt amount required to enable partial payments"
        },
        "minPartialPaymentAmount": {
          "type": "number",
          "default": 0,
          "description": "Minimum amount allowed for a single partial payment (0 = no minimum)"
        },
        "paymentFrequencies": {
          "type": "array",
          "items": {
            "type": "string",
            "enum": ["monthly", "bi-weekly", "weekly"]
          },
          "default": ["monthly"],
          "description": "Available payment plan frequencies"
        },
        "providers": {
          "type": "object",
          "description": "Payment method provider visibility configuration",
          "additionalProperties": false,
          "properties": {
            "card": {
              "type": "object",
              "description": "Card payment method configuration",
              "additionalProperties": false,
              "properties": {
                "enabled": {
                  "type": "boolean",
                  "default": true,
                  "description": "Show card payment method in customer portal"
                }
              }
            },
            "sepa": {
              "type": "object",
              "description": "SEPA direct debit payment method configuration",
              "additionalProperties": false,
              "properties": {
                "enabled": {
                  "type": "boolean",
                  "default": true,
                  "description": "Show SEPA direct debit payment method in customer portal"
                }
              }
            },
            "multibanco": {
              "type": "object",
              "description": "Multibanco payment method configuration",
              "additionalProperties": false,
              "properties": {
                "enabled": {
                  "type": "boolean",
                  "default": true,
                  "description": "Show Multibanco payment method in customer portal"
                }
              }
            },
            "mb_way": {
              "type": "object",
              "description": "MB Way payment method configuration",
              "additionalProperties": false,
              "properties": {
                "enabled": {
                  "type": "boolean",
                  "default": true,
                  "description": "Show MB Way payment method in customer portal"
                }
              }
            },
            "bacs": {
              "type": "object",
              "description": "Bacs Direct Debit payment method configuration",
              "additionalProperties": false,
              "properties": {
                "enabled": {
                  "type": "boolean",
                  "default": false,
                  "description": "Show Bacs Direct Debit payment method in customer portal"
                }
              }
            }
          }
        },
        "balanceTolerance": {
          "type": "integer",
          "default": 10,
          "description": "Balance tolerance in millicents for floating point comparisons (default: 10 = 0.01 EUR)"
        },
        "allocationStrategy": {
          "type": "string",
          "enum": ["waterfall", "pro-rata", "principal-first"],
          "default": "waterfall",
          "description": "Payment allocation strategy: waterfall (Fees → Interest → Principal), pro-rata (proportional), or principal-first (Principal → Interest → Fees)"
        },
        "planPaymentMethods": {
          "type": "array",
          "items": {
            "type": "string",
            "enum": ["card", "sepa", "multibanco", "mb_way", "bacs"]
          },
          "default": ["card"],
          "description": "Payment methods available for payment plans"
        },
        "refunds": {
          "type": "object",
          "description": "Refund controls for this client. Refunds always require two-leg (four-eyes) approval; this block only decides whether they may be raised at all.",
          "additionalProperties": false,
          "properties": {
            "allowed": {
              "type": "boolean",
              "default": false,
              "description": "Whether refunds may be raised for this client. When false, the create-refund endpoint rejects the request."
            },
            "auto_draft_on_overpayment": {
              "type": "string",
              "enum": ["off", "full_surplus_only", "any"],
              "default": "off",
              "description": "Auto-draft a refund when a payment leaves the case in credit. off: never. full_surplus_only: only when the triggering payment is wholly surplus. any: draft on any overpayment (may over-refund; the approver reviews)."
            }
          }
        }
      }
    },
    "payment_plan_tiers": {
      "type": "array",
      "description": "Payment plan tier configurations",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "properties": {
          "id": {
            "type": "string",
            "description": "Unique identifier for the tier"
          },
          "name": {
            "type": "string",
            "description": "Display name for the tier"
          },
          "description": {
            "type": "string",
            "description": "Description of the payment plan tier"
          },
          "minimumAmount": {
            "type": "number",
            "description": "Minimum amount for this tier"
          },
          "maximumAmount": {
            "type": "number",
            "description": "Maximum amount for this tier"
          },
          "maxInstallments": {
            "type": "integer",
            "description": "Maximum number of installments"
          },
          "interestRate": {
            "type": "number",
            "description": "Interest rate percentage"
          },
          "setupFee": {
            "type": "number",
            "description": "One-time setup fee"
          }
        },
        "required": ["id", "name", "minimumAmount", "maxInstallments"]
      }
    },
    "authentication": {
      "type": "object",
      "description": "Customer portal authentication configuration",
      "additionalProperties": false,
      "properties": {
        "authMode": {
          "type": "string",
          "description": "Authentication mode: early_verification (require 2FA immediately), deferred_verification (require 2FA only for restricted actions), or none (never require 2FA)",
          "enum": ["early_verification", "deferred_verification", "none"],
          "default": "none"
        },
        "allowGuestCheckout": {
          "type": "boolean",
          "description": "Allow customers to skip verification with limited permissions (early_verification mode)",
          "default": false
        },
        "twoFactorMethods": {
          "type": "array",
          "description": "Available two-factor authentication methods",
          "items": {
            "type": "string",
            "enum": ["email", "sms"]
          },
          "default": ["email", "sms"]
        },
        "restrictedPages": {
          "type": "array",
          "description": "Pages that require verification (deferred_verification mode)",
          "items": {
            "type": "string"
          },
          "default": ["/account"]
        },
        "requireVerificationForPayments": {
          "type": "boolean",
          "description": "Require verification before making payments (deferred_verification mode)",
          "default": true
        },
        "requireVerificationForPlans": {
          "type": "boolean",
          "description": "Require verification before creating payment plans (deferred_verification mode)",
          "default": true
        }
      }
    },
    "case_lifecycle": {
      "type": "object",
      "description": "Case lifecycle configuration settings",
      "additionalProperties": false,
      "properties": {
        "handback_days": {
          "type": "integer",
          "default": 60,
          "minimum": 1,
          "description": "Number of days after which a case is eligible for handback to the client"
        }
      }
    },
    "journey_transition_jdm": { "$ref": "#/$defs/zenJdm" },
    "cooldown_calculation_jdm": { "$ref": "#/$defs/zenJdm" },
    "retry_limit_check_jdm": { "$ref": "#/$defs/zenJdm" },
    "quiet_hours_adjustment_jdm": { "$ref": "#/$defs/zenJdm" },
    "escalation_check_jdm": { "$ref": "#/$defs/zenJdm" },
    "carePolicy": {
      "type": "object",
      "default": {},
      "description": "Customer-care cascade gates. Omitted compiles to all-false: no skip, no automation pause.",
      "additionalProperties": false,
      "properties": {
        "onActiveVulnerability": {
          "type": "object",
          "default": {},
          "description": "What happens when the customer has an active vulnerability mark.",
          "additionalProperties": false,
          "properties": {
            "skipHandback": {
              "type": "boolean",
              "default": false,
              "description": "Skip placement handback while the customer mark is active."
            },
            "blockAutomation": {
              "type": "boolean",
              "default": false,
              "description": "Pause journey automation while the customer mark is active. Default false — wiring the mark without this gate would pause every Vulnerability note."
            }
          }
        }
      }
    },
    "budget_assessment": {
      "type": "object",
      "description": "Income & expenditure (affordability) assessment requirements. A tier is required once the plan's length in months or the debt balance crosses its threshold, whichever triggers first.",
      "additionalProperties": false,
      "properties": {
        "enabled": {
          "type": "boolean",
          "default": false,
          "description": "Require an affordability assessment at all. When false the whole flow is off and payment plans are offered without one, which is how markets outside the UK operate."
        },
        "validityWindowDays": {
          "type": "integer",
          "default": 90,
          "minimum": 1,
          "description": "How long a completed assessment stays reusable before the customer is asked to refresh it. Stamped onto the assessment as valid_until when it completes."
        },
        "acceptableReferralReasons": {
          "type": "array",
          "items": {
            "type": "string"
          },
          "description": "Referral reasons an agent may accept an offer on as submitted. Values are `plan_request_decision_reason` members; anything not listed is refused, and so is an unset or empty list — an operator who has not chosen cannot have one chosen for them. Deliberately carries no `default`: scope merging replaces an array outright rather than unioning it, so a narrower scope that materialised a default [] would silently override the operator list and lock acceptance, which reads identically to correct fail-closed behaviour. Absent already means the same thing, without the clobber. Not enum-constrained either — a second copy of the enum here would be free to drift from the type the rows actually carry."
        },
        "short": {
          "type": "object",
          "description": "Thresholds above which a short assessment is required. Both null means this tier never triggers.",
          "additionalProperties": false,
          "properties": {
            "months": {
              "type": ["integer", "null"],
              "default": null,
              "minimum": 3,
              "description": "Plan length in months above which a short assessment is required (null = not gated on length). The floor of 3 is deliberate: the rule is 'required when length exceeds the threshold', so it guarantees plans of three months or less always clear the length axis."
            },
            "balance": {
              "type": ["number", "null"],
              "default": null,
              "minimum": 0,
              "description": "Debt balance above which a short assessment is required (null = not gated on balance)"
            },
            "questionSet": {
              "type": "string",
              "enum": ["low", "medium", "high"],
              "default": "medium",
              "description": "Which shared question set this tier asks. Held as config rather than hard-coded so a market can change the depth of a tier without a schema change."
            }
          }
        },
        "full": {
          "type": "object",
          "description": "Thresholds above which a full assessment is required. Both null means this tier never triggers. Named for its depth, not for any one market's instrument.",
          "additionalProperties": false,
          "properties": {
            "months": {
              "type": ["integer", "null"],
              "default": null,
              "minimum": 3,
              "description": "Plan length in months above which a full assessment is required (null = not gated on length). Should exceed the short tier's threshold on the same axis, but nothing here can enforce that — JSON Schema cannot compare sibling properties, and a fragment is partial, so the ordering can only be checked on compiled config."
            },
            "balance": {
              "type": ["number", "null"],
              "default": null,
              "minimum": 0,
              "description": "Debt balance above which a full assessment is required (null = not gated on balance)"
            },
            "questionSet": {
              "type": "string",
              "enum": ["low", "medium", "high"],
              "default": "high",
              "description": "Which shared question set this tier asks. In the UK the full tier is the Standard Financial Statement line items."
            }
          }
        },
        "operationalReview": {
          "type": "object",
          "description": "Referral to a specialist after an assessment completes. A customer who flagged a situation is always referred, regardless of these settings.",
          "additionalProperties": false,
          "properties": {
            "disposableIncomePct": {
              "type": ["number", "null"],
              "default": null,
              "minimum": 0,
              "maximum": 100,
              "description": "Refer the offer when the monthly plan amount exceeds this percentage of the customer's monthly disposable income (null = never refer on affordability alone)"
            }
          }
        }
      }
    }
  },
  "$defs": {
    "zenJdm": {
      "type": "object",
      "description": "Loose Zen Engine JDM — simple decision table or full GoRules graph, used for journey transitions, cooldown/retry/quiet-hours/escalation checks. Runtime validates structure; schema only forbids non-objects. Missing → evaluator pauses the case.",
      "additionalProperties": true,
      "properties": {
        "key": { "type": "string" },
        "name": { "type": "string" },
        "hitPolicy": { "type": "string" },
        "input": {
          "type": "array",
          "items": {
            "type": "object",
            "required": ["id", "name"],
            "properties": { "id": { "type": "string" }, "name": { "type": "string" } },
            "additionalProperties": true
          }
        },
        "output": {
          "type": "array",
          "items": {
            "type": "object",
            "required": ["id", "name"],
            "properties": { "id": { "type": "string" }, "name": { "type": "string" } },
            "additionalProperties": true
          }
        },
        "rules": { "type": "array" }
      }
    }
  }
}
